import './empty-state.css';

/**
 * Shared empty/loading/no-match state helpers for list pages.
 *
 * Three states are unified here so every list page (recipes, ingredients,
 * inventory, shopping) presents loading, no-data, and no-search-match states
 * with identical wording structure and styling:
 *
 *   - stateLoading(noun)       → "Loading <noun>…"      (while fetching)
 *   - stateEmpty(message)      → a muted "nothing yet"  (no data at all)
 *   - stateNoMatches({...})    → message + Clear search  (search excludes all)
 *
 * Each returns a detached DOM node the caller drops into its list container.
 */

/** A muted, centred message node — the base for loading + empty states. */
function messageNode(text) {
  const p = document.createElement('p');
  p.className = 'empty-state-message';
  p.textContent = text;
  return p;
}

/** Loading state, e.g. stateLoading('recipes') → "Loading recipes…". */
export function stateLoading(noun) {
  const el = document.createElement('div');
  el.className = 'empty-state';
  el.appendChild(messageNode(`Loading ${noun}…`));
  return el;
}

/** No-data state: a single muted line explaining the list is empty. */
export function stateEmpty(message) {
  const el = document.createElement('div');
  el.className = 'empty-state';
  el.appendChild(messageNode(message));
  return el;
}

/**
 * No-search-match state: a muted line plus a "Clear search" button.
 * @param {object} opts
 * @param {string} opts.message  - the muted explanation line
 * @param {() => void} opts.onClear - invoked when "Clear search" is clicked
 */
export function stateNoMatches({ message, onClear }) {
  const el = document.createElement('div');
  el.className = 'empty-state';
  el.appendChild(messageNode(message));

  const clear = document.createElement('button');
  clear.type = 'button';
  clear.className = 'secondary';
  clear.textContent = 'Clear search';
  clear.addEventListener('click', onClear);
  el.appendChild(clear);

  return el;
}
